import { isOfficialHref, isSponsorHref } from 'utils/story-links';

const FOLLOW_LABEL = 'Follow link';
const PAID_LABEL = 'Paid link: always nofollow sponsored';

// CKEditor plugin for the story's link balloon: the "Follow link" switch starts
// on for official sources and off for everything else, and a paid link is
// locked to sponsored. The hidden "nofollow" decorator stores the switch's off
// state, so an explicit nofollow differs from a link nobody chose for.
export const storyLinkRelPlugin = (sponsorHosts) =>
    class StoryLinkRel {
        constructor(editor) {
            this.editor = editor;
        }

        afterInit() {
            const { editor } = this;
            const command = editor.commands.get('link');
            // CKEditor prefixes decorator ids: "follow" becomes "linkFollow".
            const decorator = (id) => command?.manualDecorators.get(id);
            const sponsored = decorator('linkSponsored');
            const follow = decorator('linkFollow');
            const nofollow = decorator('linkNofollow');
            if (!sponsored || !follow || !nofollow) return;

            // LinkFormView has no public accessor for its switches in v35; if a
            // CKEditor upgrade moves them, the balloon keeps its plain switches.
            const form = editor.plugins.has('LinkUI')
                ? editor.plugins.get('LinkUI').formView
                : null;
            const switches = form?._manualDecoratorSwitches;
            const switchFor = (id) =>
                typeof switches?.find === 'function'
                    ? switches.find((view) => view.name === id)
                    : null;
            const followSwitch = switchFor('linkFollow');
            const sponsoredSwitch = switchFor('linkSponsored');
            const nofollowSwitch = switchFor('linkNofollow');
            const wired = Boolean(
                followSwitch &&
                    sponsoredSwitch &&
                    nofollowSwitch &&
                    form.urlInputView?.fieldView &&
                    editor.plugins.has('ContextualBalloon')
            );

            if (wired) {
                const balloon = editor.plugins.get('ContextualBalloon');
                const field = form.urlInputView.fieldView;
                nofollowSwitch.isVisible = false;

                let url = '';
                let chosen = false;
                let autoSponsored = false;
                const showToggle = (visible) => {
                    const toggle =
                        followSwitch.element?.querySelector(
                            '.ck-button__toggle'
                        );
                    if (toggle) toggle.style.display = visible ? '' : 'none';
                };
                const sync = () => {
                    const paidHost = isSponsorHref(url, sponsorHosts());
                    if (paidHost && !sponsored.value) {
                        autoSponsored = true;
                        sponsored.value = true;
                    } else if (!paidHost && autoSponsored) {
                        autoSponsored = false;
                        sponsored.value = false;
                    }
                    sponsoredSwitch.isEnabled = !paidHost;
                    const paid = paidHost || Boolean(sponsored.value);
                    followSwitch.isEnabled = !paid;
                    followSwitch.label = paid ? PAID_LABEL : FOLLOW_LABEL;
                    showToggle(!paid);
                    if (paid) follow.value = false;
                    else if (!chosen) follow.value = isOfficialHref(url);
                };

                balloon.on('change:visibleView', (evt, name, view) => {
                    if (view !== form) return;
                    url = command.value || '';
                    chosen = Boolean(follow.value || nofollow.value);
                    autoSponsored = false;
                    sync();
                });
                field.on('input', () => {
                    url = field.element.value;
                    sync();
                });
                followSwitch.on('execute', () => {
                    chosen = true;
                });
                sponsored.on('change:value', () => {
                    if (balloon.visibleView === form) sync();
                });
            }

            command.on(
                'execute',
                (evt, args) => {
                    const ids = args[1];
                    if (!ids || !('linkFollow' in ids)) return;
                    if (
                        ids.linkSponsored ||
                        isSponsorHref(args[0], sponsorHosts())
                    ) {
                        args[1] = {
                            ...ids,
                            linkSponsored: true,
                            linkFollow: false,
                            linkNofollow: false,
                        };
                    } else if (wired) {
                        args[1] = { ...ids, linkNofollow: !ids.linkFollow };
                    } else if (ids.linkFollow && ids.linkNofollow) {
                        args[1] = { ...ids, linkFollow: false };
                    }
                },
                { priority: 'high' }
            );
        }
    };
